import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
} from "../../bids/analytics/format";
import { AdCampaignStatus, AdCampaignStatusLabels } from "../../../models/adCampaign";
import { TermHint } from "../components/TermHint";
import { ueCampaignsApi } from "../unitEconomicsApi";
import { CampaignDetail } from "./CampaignDetail";

type Props = {
    isActive: boolean;
};

function statusColor(status: AdCampaignStatus): "neutral" | "success" | "warning" | "danger" {
    switch (status) {
        case AdCampaignStatus.Active:
            return "success";
        case AdCampaignStatus.Paused:
            return "warning";
        case AdCampaignStatus.Finished:
            return "danger";
        default:
            return "neutral";
    }
}

function formatRoas(roas: number | string | null | undefined): string {
    if (roas === null || roas === undefined || roas === "") {
        return "—";
    }
    const n = Number(roas);
    if (!Number.isFinite(n)) {
        return "—";
    }
    return `${n.toFixed(2)}×`;
}

export function CampaignsTab(props: Props) {
    const { isActive } = props;
    const [selectedId, setSelectedId] = React.useState<number | null>(null);

    const [listState, refreshList] = useAsyncBlock(
        () => ueCampaignsApi(),
        isActive && selectedId === null,
        [selectedId]
    );

    if (selectedId != null) {
        return (
            <CampaignDetail
                campaignId={selectedId}
                onBack={() => setSelectedId(null)}
            />
        );
    }

    const data = listState.data;
    const campaigns = data?.campaigns ?? [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={listState.loading} onClick={refreshList}>
                    Обновить
                </Button>
            </Box>
            {listState.loading && !data && <BlockLoading />}
            {listState.error && <BlockError message={listState.error} onRetry={refreshList} />}
            {data && (
                <AnalyticsCard
                    title={<>Рекламные кампании</>}
                    subtitle={
                        <>
                            Каждая строка — итоги кампании за весь её срок. Нажмите на кампанию, чтобы
                            открыть подробности. <TermHint term="campCac" /> и{" "}
                            <TermHint term="campRoas" /> считаются, только если указан бюджет.
                        </>
                    }
                    snapshotLabel="lifetime"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Строка — всё, что сделали пришедшие по кампании от прихода до конца
                                    кампании, а не срез за выбранный период. Цифры совпадают с карточкой
                                    кампании.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Пример:</strong> пришли 100, активировались 40 (40%), доход
                                    площадки 12 000 ₽ — доход на пришедшего 120 ₽. Бюджет 10 000 ₽ — цена
                                    привлечения 100 ₽, окупаемость бюджета (12 000 − 1 000 промо) ÷ 10 000 =
                                    1.10×.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    Сравнивайте каналы по доле активации и доходу, а не только по числу
                                    пришедших.
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: <TermHint term="campActivationRate" />,
                                    ifGrows: "больше пришедших делают первый шаг.",
                                    ifFalls: "приходят «пустые» люди — смотрите страницу кампании и регистрацию.",
                                },
                                {
                                    signal: (
                                        <>
                                            <TermHint term="campArpu" /> /{" "}
                                            <TermHint term="campContribution" />
                                        </>
                                    ),
                                    ifGrows: "пришедшие ценнее: больше дохода на человека или в сумме.",
                                    ifFalls: "доход слабеет — смотрите сделки и пополнения в карточке.",
                                },
                                {
                                    signal: <TermHint term="campCac" />,
                                    growsIsGood: false,
                                    ifGrows: "люди обходятся дороже — сверьте окупаемость и активацию.",
                                    ifFalls: "люди дешевле — проверьте, не упала ли доля активации.",
                                },
                                {
                                    signal: <TermHint term="campRoas" />,
                                    ifGrows: "каждый рубль бюджета приносит больше дохода.",
                                    ifFalls: "доход слабее относительно бюджета.",
                                },
                            ]}
                        />
                    }
                >
                    {campaigns.length === 0 ? (
                        <EmptyState hint="Нет кампаний. Создайте кампанию в разделе «Рекламные кампании»." />
                    ) : (
                        <ScrollTable>
                            <Table size="sm" hoverRow>
                                <thead>
                                    <tr>
                                        <th>Кампания</th>
                                        <th>Канал</th>
                                        <th>Статус</th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campRegistered" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campActivated" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campActivationRate" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campContribution" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campArpu" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campCac" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campRoas" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campPromoActivationsAndRedemptions" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campPromoCost" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campNetResult" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map((c) => (
                                        <tr
                                            key={c.campaignId}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setSelectedId(c.campaignId)}
                                        >
                                            <td>
                                                <Link
                                                    component="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedId(c.campaignId);
                                                    }}
                                                >
                                                    {c.name}
                                                </Link>
                                            </td>
                                            <td>{c.channel}</td>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={statusColor(c.status)}
                                                >
                                                    {AdCampaignStatusLabels[c.status]}
                                                </Chip>
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatInt(c.registered)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatInt(c.activated)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatRatePercent(c.activationRate)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatMoneyKopecks(c.totalContribution)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatMoneyKopecks(c.arpu)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {c.cac != null ? formatMoneyKopecks(c.cac) : "—"}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatRoas(c.roas)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {c.promoActivations
                                                    ? `${formatInt(c.promoActivations)} / ${formatInt(c.promoRedemptions ?? 0)}`
                                                    : "—"}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {c.promoCostKopecks ? formatMoneyKopecks(c.promoCostKopecks) : "—"}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {c.netContribution != null
                                                    ? formatMoneyKopecks(c.netContribution)
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}
